import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import type { PlainWorkflowExecutionAction, PlainWorkflowExecutionActionOutput } from "@/queries/automation-queries";
import { downloadCSV, formatOutputValueForCSV, generateCSV } from "@/utils/csv-export";
import { autoPaginate } from "@/queries/pagination";
import { useMutation } from "@tanstack/react-query";
import { ListWorkflowExecutionOutputsRequest } from "gitpod-next-api/gitpod/v1/workflow_pb";
import { toPlainMessage } from "@bufbuild/protobuf";
import { toast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";

type ExportParams = {
    workflowExecutionId: string;
    actions: PlainWorkflowExecutionAction[];
};

/**
 * Extracts the repository URL from a workflow execution action's context.
 * Returns empty string if no context URL is available.
 */
function getRepositoryUrl(action: PlainWorkflowExecutionAction): string {
    const context = action.spec?.context;
    if (context?.context.case === "contextUrl") {
        return context.context.value.url;
    }
    return "";
}

export const useExportWorkflowOutputsCSV = () => {
    const api = useGitpodAPI();

    return useMutation({
        mutationFn: async ({ workflowExecutionId, actions }: ExportParams) => {
            // Fetch all outputs using pagination
            const request = new ListWorkflowExecutionOutputsRequest({
                filter: { workflowExecutionIds: [workflowExecutionId] },
            });

            const outputs = await autoPaginate(
                (params) => api.workflowService.listWorkflowExecutionOutputs(params),
                request,
                (response) => response.outputs,
            );

            const allOutputs = outputs.map((output) => toPlainMessage(output));

            // Build outputs map for quick lookup
            const outputsMap = new Map<string, PlainWorkflowExecutionActionOutput>();
            for (const output of allOutputs) {
                outputsMap.set(output.actionId, output);
            }

            // Extract all unique output keys and sort alphabetically
            const outputKeysSet = new Set<string>();
            for (const output of allOutputs) {
                for (const key of Object.keys(output.values)) {
                    outputKeysSet.add(key);
                }
            }
            const outputKeys = Array.from(outputKeysSet).sort();

            // Build CSV header - include Repository URL column
            const headers = ["Action", "Repository URL", ...outputKeys];

            // Build CSV rows (using same action order as table)
            const rows: string[][] = [];
            for (const action of actions) {
                const actionName = action.metadata?.actionName || action.id;
                const repositoryUrl = getRepositoryUrl(action);
                const output = outputsMap.get(action.id);

                const row = [actionName, repositoryUrl];
                for (const key of outputKeys) {
                    const value = output?.values[key];
                    if (value) {
                        row.push(formatOutputValueForCSV(value));
                    } else {
                        row.push("-");
                    }
                }
                rows.push(row);
            }

            // Generate CSV and trigger download
            const csvContent = generateCSV(headers, rows);
            const filename = `automation-outputs-${workflowExecutionId}.csv`;
            downloadCSV(csvContent, filename);
        },
        onError: (error) => {
            toast({
                title: "Failed to export CSV",
                description: formatError(error),
            });
        },
    });
};
